import { FC, useEffect, useState } from "react";

import Stack from "@mui/material/Stack";
import { OfferedFulfillmentMethod, offersPickup, offersShipping } from "@simplyk/common";
import dynamic from "next/dynamic";
import { useForm, useWatch } from "react-hook-form";

import { useLiveFormEditorContext } from "../../common/LiveFormEditorContext";
import { useUpdateTicketingDataInPreview } from "../../common/useUpdateLiveTicketingPreview/useUpdateTicketingDataInPreview";
import { useFormSaveAndNavigate } from "../useFormSaveAndNavigate";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { trpc } from "@/common/helpers/trpc";
import { isMinOrZero, isRequired } from "@/common/helpers/validators";
import { useGoogleMapsAutocomplete } from "@/common/hooks/useGoogleMapsAutocomplete";
import { useTranslate } from "@/common/hooks/useTranslate";
import { TicketingOutput } from "@/common/types/trpc";
import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { LiveEditorToggle } from "@/components/LiveEditorElements/LiveEditorToggle/LiveEditorToggle";
import { DashboardAmountInput } from "@/dashboard-design-system/DashboardAmountInput";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardInfobox } from "@/dashboard-design-system/Infobox";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { FormStepContainer } from "@/features/LiveFormEditor/Steps/FormStepContainer";
import { Add } from "@/icons/outlined";

const LeafletMap = dynamic(() => import("@/components/LeafletMap/LeafletMap"), { ssr: false });

const PICKUP_INSTRUCTIONS_MAX_LENGTH = 200;

export type OrderFulfillmentFormData = {
  offeredPickup: boolean;
  offeredShipping: boolean;
  pickupInstructions: string;
  shippingFeeAmount: number;
  address: string | null;
  coordinates: { latitude: number; longitude: number } | null;
};

const getOfferedMethodsFromFormData = (data: OrderFulfillmentFormData): OfferedFulfillmentMethod[] => [
  ...(data.offeredPickup ? [OfferedFulfillmentMethod.Pickup] : []),
  ...(data.offeredShipping ? [OfferedFulfillmentMethod.Shipping] : []),
];

const getFormCoordinates = (
  coordinates: { latitude?: number | null; longitude?: number | null } | null | undefined
): { latitude: number; longitude: number } | null => {
  const latitude = coordinates?.latitude;
  const longitude = coordinates?.longitude;
  if (typeof latitude !== "number" || typeof longitude !== "number") {
    return null;
  }
  return { latitude, longitude };
};

export const TicketingLiveEditorStepOrderFulfillment: FC = () => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { organization, organizationCurrency } = useCurrentUserContext();
  const { apiFormObject: apiTicketing } = useLiveFormEditorContext<"ticketing">();

  const offered = apiTicketing.offeredFulfillmentMethods ?? [];
  const [showPickupInstructions, setShowPickupInstructions] = useState(
    Boolean(apiTicketing.pickupInstructions?.trim())
  );

  const form = useForm<OrderFulfillmentFormData>({
    defaultValues: {
      offeredPickup: offersPickup(offered),
      offeredShipping: offersShipping(offered),
      pickupInstructions: apiTicketing.pickupInstructions ?? "",
      shippingFeeAmount: apiTicketing.shippingFeeAmount ?? 0,
      address: apiTicketing.address ?? null,
      coordinates: getFormCoordinates(apiTicketing.coordinates),
    },
  });

  const offeredPickup = useWatch({ control: form.control, name: "offeredPickup" });

  const { addressString, addressCoordinates, hasAddress } = useGoogleMapsAutocomplete({
    defaultAddressCoordinates: (() => {
      const coordinates = getFormCoordinates(apiTicketing.coordinates);
      return coordinates ? { lat: coordinates.latitude, lng: coordinates.longitude } : undefined;
    })(),
    defaultAddressString: apiTicketing.address ?? undefined,
    addressInputId: "order-fulfillment-address-input",
    organizationCountry: organization?.country,
    disabled: !offeredPickup,
    includeVenueName: true,
  });

  useEffect(() => {
    form.setValue("address", addressString ?? null, { shouldDirty: true });
    form.setValue(
      "coordinates",
      addressCoordinates ? { latitude: addressCoordinates.lat, longitude: addressCoordinates.lng } : null,
      { shouldDirty: true }
    );
  }, [addressString, addressCoordinates, form]);

  const offeredShipping = useWatch({ control: form.control, name: "offeredShipping" });
  const pickupInstructions = useWatch({ control: form.control, name: "pickupInstructions" }) ?? "";

  const updateConfig = trpc.updateFormFulfillmentConfig.useMutation();
  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<OrderFulfillmentFormData>({
    type: "ticketing",
    form,
    saveData: async (data) => {
      const methods = getOfferedMethodsFromFormData(data);
      const result = await updateConfig.mutateAsync({
        ticketingId: apiTicketing.id,
        offeredFulfillmentMethods: methods,
        pickupInstructions: data.pickupInstructions || null,
        shippingFeeAmount: data.shippingFeeAmount ?? 0,
        address: data.offeredPickup ? data.address : (apiTicketing.address ?? null),
        coordinatesLatitude: data.offeredPickup
          ? (data.coordinates?.latitude ?? null)
          : (apiTicketing.coordinates?.latitude ?? null),
        coordinatesLongitude: data.offeredPickup
          ? (data.coordinates?.longitude ?? null)
          : (apiTicketing.coordinates?.longitude ?? null),
      });
      if (result.error || !result.data) {
        return { data: null, error: result.error };
      }
      return {
        data: {
          ...apiTicketing,
          offeredFulfillmentMethods: result.data.offeredFulfillmentMethods,
          pickupInstructions: result.data.pickupInstructions,
          shippingFeeAmount: result.data.shippingFeeAmount,
          address: result.data.address ?? apiTicketing.address,
          coordinates:
            typeof result.data.coordinatesLatitude === "number" && typeof result.data.coordinatesLongitude === "number"
              ? { latitude: result.data.coordinatesLatitude, longitude: result.data.coordinatesLongitude }
              : apiTicketing.coordinates,
        } as TicketingOutput,
        error: null,
      };
    },
  });

  useUpdateTicketingDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      liveDataToMutate.ticketing.offeredFulfillmentMethods = getOfferedMethodsFromFormData(formData);
      liveDataToMutate.ticketing.pickupInstructions = formData.pickupInstructions || null;
      liveDataToMutate.ticketing.shippingFeeAmount = formData.shippingFeeAmount ?? 0;
      if (formData.offeredPickup && formData.address) {
        liveDataToMutate.ticketing.address = formData.address;
      }
      liveDataToMutate.placeholders.isLocation = formData.offeredPickup && !formData.address;
    },
  });

  return (
    <FormStepContainer
      title={t("dashboard", "liveFormEditor.stepOrderFulfillment.title")}
      description={t("dashboard", "liveFormEditor.stepOrderFulfillment.description")}
      form={form}
      actionsProps={{ onBack: handleSaveAndBack, onNext: handleSaveAndNext }}
    >
      <Stack gap={2}>
        <LiveEditorBlock rowGap={2}>
          <LiveEditorToggle
            name="offeredPickup"
            label={t("dashboard", "liveFormEditor.stepOrderFulfillment.pickupToggle")}
            control={form.control}
          />
          {offeredPickup && (
            <Stack gap={2}>
              <DashboardTextField
                control={form.control}
                name="address"
                id="order-fulfillment-address-input"
                label={t("dashboard", "liveFormEditor.stepOrderFulfillment.pickupAddress")}
                placeholder={t("dashboard", "liveFormEditor.stepOrderFulfillment.pickupAddressPlaceholder")}
                rules={[isRequired(true, t("common", "isRequired"))]}
              />
              {hasAddress && addressCoordinates && (
                <LeafletMap
                  data-test="order-fulfillment-map"
                  latitude={addressCoordinates.lat}
                  longitude={addressCoordinates.lng}
                />
              )}
              {!showPickupInstructions && (
                <DashboardButton
                  variant="outlined"
                  vibe="neutral"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setShowPickupInstructions(true)}
                  sx={{ alignSelf: "flex-start" }}
                >
                  {t("dashboard", "liveFormEditor.stepOrderFulfillment.addPickupInstructions")}
                </DashboardButton>
              )}
              {showPickupInstructions && (
                <Stack gap={0.5}>
                  <DashboardTypography variant="body2" vibe="text-neutral-moderate">
                    {t("dashboard", "liveFormEditor.stepOrderFulfillment.pickupInstructionsLabel")}
                  </DashboardTypography>
                  <DashboardTextField
                    control={form.control}
                    name="pickupInstructions"
                    multiline
                    rows={3}
                    placeholder={t("dashboard", "liveFormEditor.stepOrderFulfillment.pickupInstructionsPlaceholder")}
                    inputProps={{ maxLength: PICKUP_INSTRUCTIONS_MAX_LENGTH }}
                  />
                  <DashboardTypography variant="body2" vibe="text-neutral-moderate">
                    {t("dashboard", "liveFormEditor.stepOrderFulfillment.pickupInstructionsCounter", {
                      count: pickupInstructions.length,
                    })}
                  </DashboardTypography>
                </Stack>
              )}
            </Stack>
          )}
        </LiveEditorBlock>

        <LiveEditorBlock rowGap={2}>
          <LiveEditorToggle
            name="offeredShipping"
            label={t("dashboard", "liveFormEditor.stepOrderFulfillment.shippingToggle")}
            control={form.control}
          />
          {offeredShipping && (
            <Stack gap={2}>
              <DashboardAmountInput
                control={form.control}
                name="shippingFeeAmount"
                locale={locale}
                currency={organizationCurrency}
                label={t("dashboard", "liveFormEditor.stepOrderFulfillment.shippingFee")}
                rules={[isMinOrZero(0, t("dashboard", "ticketing.rateAmountMinimum"))]}
                data-test="order-fulfillment-shipping-fee-input"
              />
              <DashboardInfobox vibe="brand" size="small">
                {t("dashboard", "liveFormEditor.stepOrderFulfillment.shippingInfobox")}
              </DashboardInfobox>
            </Stack>
          )}
        </LiveEditorBlock>
      </Stack>
    </FormStepContainer>
  );
};
